import { RefObject, useEffect, useState } from 'react';

export interface ScrollShadeState {
	/** There is hidden content above the viewport (scrolled down). */
	top: boolean;
	/** There is hidden content below the viewport (more to scroll). */
	bottom: boolean;
}

/**
 * Tracks whether a scroll container has clipped content above/below its
 * viewport, so a caller can show/hide scroll shadows accordingly.
 *
 * Works in every browser (no scroll-driven animations): it recomputes on
 * scroll, on resize of the container and its children (`ResizeObserver`), and
 * when children are added/removed (`MutationObserver`, e.g. a dropdown
 * expanding). The shades stay hidden whenever the content fits.
 *
 * @param ref The scrollable element.
 * @param threshold Slack in px before a side is considered scrollable.
 */
const useScrollShade = (ref: RefObject<HTMLElement | null>, threshold = 1): ScrollShadeState => {
	const [state, setState] = useState<ScrollShadeState>({ top: false, bottom: false });

	useEffect(() => {
		const el = ref.current;

		if (!el) {
			return;
		}

		const update = () => {
			const { scrollTop, scrollHeight, clientHeight } = el;
			const scrollable = scrollHeight - clientHeight > threshold;
			const next: ScrollShadeState = {
				top: scrollable && scrollTop > threshold,
				bottom: scrollable && scrollTop + clientHeight < scrollHeight - threshold,
			};
			setState((prev) => (prev.top === next.top && prev.bottom === next.bottom ? prev : next));
		};

		const resizeObserver = new ResizeObserver(update);
		const observeAll = () => {
			resizeObserver.disconnect();
			resizeObserver.observe(el);
			for (const child of Array.from(el.children)) {
				resizeObserver.observe(child);
			}
		};

		const mutationObserver = new MutationObserver(() => {
			observeAll();
			update();
		});

		// `observeAll` seeds the initial state: a ResizeObserver fires its callback
		// once when observation starts. (Calling `update()` here directly would be
		// a synchronous setState in the effect body — an extra render and a lint
		// warning.)
		observeAll();
		el.addEventListener('scroll', update, { passive: true });
		mutationObserver.observe(el, { childList: true, subtree: true });

		return () => {
			el.removeEventListener('scroll', update);
			resizeObserver.disconnect();
			mutationObserver.disconnect();
		};
	}, [ref, ref.current?.clientHeight, threshold]);

	return state;
};

export default useScrollShade;
